import React, { useState, useRef, useEffect } from 'react'
import { TooltipProps } from './Tooltip.types'
import { TooltipWrapper, TooltipContent } from './Tooltip.styles'

export const Tooltip: React.FC<TooltipProps> = ({
  content,
  children,
  placement = 'top',
  delay = 200,
  maxWidth = 250,
}) => {
  const [isVisible, setIsVisible] = useState(false)
  const [actualPlacement, setActualPlacement] = useState(placement)
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)
  const wrapperRef = useRef<HTMLDivElement>(null)
  const contentRef = useRef<HTMLDivElement>(null)

  const handleMouseEnter = () => {
    timeoutRef.current = setTimeout(() => {
      setIsVisible(true)
    }, delay)
  }

  const handleMouseLeave = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }
    setIsVisible(false)
  }

  useEffect(() => {
    if (isVisible && contentRef.current && wrapperRef.current) {
      const rect = wrapperRef.current.getBoundingClientRect()
      const tooltipRect = contentRef.current.getBoundingClientRect()
      const viewportWidth = window.innerWidth
      const viewportHeight = window.innerHeight

      let newPlacement = placement

      // Check if tooltip goes off screen and adjust
      if (
        placement === 'right' &&
        rect.right + tooltipRect.width + 20 > viewportWidth
      ) {
        newPlacement = 'left'
      } else if (
        placement === 'left' &&
        rect.left - tooltipRect.width - 20 < 0
      ) {
        newPlacement = 'right'
      } else if (
        placement === 'top' &&
        rect.top - tooltipRect.height - 20 < 0
      ) {
        newPlacement = 'bottom'
      } else if (
        placement === 'bottom' &&
        rect.bottom + tooltipRect.height + 20 > viewportHeight
      ) {
        newPlacement = 'top'
      }

      if (newPlacement !== actualPlacement) {
        setActualPlacement(newPlacement)
      }
    }
  }, [isVisible, placement, actualPlacement])

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [])

  return (
    <TooltipWrapper
      ref={wrapperRef}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {children}
      {isVisible && content && (
        <TooltipContent
          ref={contentRef}
          $placement={actualPlacement}
          $maxWidth={maxWidth}
        >
          {content}
        </TooltipContent>
      )}
    </TooltipWrapper>
  )
}
